import { b2bProviders, EmailProvider } from "@/types/inboxPlacements.types";

import {
  b2cProviders,
  PlacementType,
  ProviderDetailedResult,
} from "@/types/inboxPlacements.types";

export type SummaryStats = {
  total: number;
  inbox: {
    count: number;
    percentage: number;
  };
  spam: {
    count: number;
    percentage: number;
  };
  unreceived: {
    count: number;
    percentage: number;
  };
};

export const calculateSummaryStats = (
  providers: ProviderDetailedResult[],
  providerType: PlacementType
): SummaryStats => {
  if (!providers || providers.length === 0) {
    return {
      total: 0,
      inbox: { count: 0, percentage: 0 },
      spam: { count: 0, percentage: 0 },
      unreceived: { count: 0, percentage: 0 },
    };
  }

  // Since we're using B2B API but showing all providers, include both B2B and B2C providers
  // Filter providers by type - but for B2B, include all providers (both B2B and B2C)
  const filteredProviders = providers.filter((provider) => {
    if (providerType === PlacementType.B2C) {
      return b2cProviders.includes(provider.provider as EmailProvider);
    } else {
      // For B2B API, include both B2B and B2C providers
      return (
        b2bProviders.includes(provider.provider as EmailProvider) ||
        b2cProviders.includes(provider.provider as EmailProvider)
      );
    }
  });

  // Initialize counters
  let totalEmails = 0;
  let inboxCount = 0;
  let spamCount = 0;
  let unreceivedCount = 0;

  // Process each provider's data
  filteredProviders.forEach((provider) => {
    const emailCount = provider.emailCount || 0;
    totalEmails += emailCount;

    // Calculate counts based on percentages and email counts
    inboxCount += Math.round((provider.inboxPercentage / 100) * emailCount);
    spamCount += Math.round((provider.spamPercentage / 100) * emailCount);
    unreceivedCount += Math.round(
      (provider.unreceivedPercentage / 100) * emailCount
    );
  });

  // Calculate percentages for the overall summary
  const inboxPercentage =
    totalEmails > 0 ? (inboxCount / totalEmails) * 100 : 0;
  const spamPercentage = totalEmails > 0 ? (spamCount / totalEmails) * 100 : 0;
  const unreceivedPercentage =
    totalEmails > 0 ? (unreceivedCount / totalEmails) * 100 : 0;

  return {
    total: totalEmails,
    inbox: { count: inboxCount, percentage: inboxPercentage },
    spam: { count: spamCount, percentage: spamPercentage },
    unreceived: {
      count: unreceivedCount,
      percentage: unreceivedPercentage,
    },
  };
};

export const deriveChartData = (
  providers: ProviderDetailedResult[],
  providerType: PlacementType,
  providerPriorityMap: { [k: string]: number }
) => {
  if (!providers || providers.length === 0) {
    return [];
  }

  return providers
    .filter((provider) => {
      // Filter based on provider type (B2C/B2B)
      if (providerType === PlacementType.B2C) {
        return b2cProviders.includes(provider.provider as EmailProvider);
      } else {
        return b2bProviders.includes(provider.provider as EmailProvider);
      }
    })

    .sort((a, b) => {
      const priorityA = providerPriorityMap[a.provider as EmailProvider] ?? 999;
      const priorityB = providerPriorityMap[b.provider as EmailProvider] ?? 999;
      return priorityA - priorityB;
    })
    .map((provider) => {
      return {
        month: provider.displayName,
        Inbox: Math.round(provider.inboxPercentage),
        Spam: Math.round(provider.spamPercentage),
        Unreceived: Math.round(provider.unreceivedPercentage),
        Total: provider.emailCount || 0,
      };
    });
};
